import { useQuery } from "@tanstack/react-query";

import { ApiError } from "@/api/client";
import { meApi } from "@/api/me";
import { queryKeys } from "@/api/query-keys";
import { type UserResponse } from "@/api/users";
import { useAuth } from "@/providers/auth-provider";

/**
 * Fetches the signed-in user's combined Kinde + MongoDB profile from
 * `/me`. Use this instead of `useUser(authUser.id)` anywhere the
 * dashboard only needs the current user's own data — `/me` is ungated
 * where `/users/{id}` requires `user:access`.
 *
 * The query is keyed by the Kinde auth id so the cache still segments
 * per-user if the session switches.
 */
export const useMe = () => {
	const { authUser } = useAuth();

	const {
		data: userData,
		error: userError,
		isError: isUserError,
		isLoading: isLoadingUser,
		refetch: refetchUser,
	} = useQuery<UserResponse, Error>({
		enabled: !!authUser?.id,
		queryFn: () => meApi.get(),
		queryKey: [...queryKeys.me.all, authUser?.id ?? ""],
		retry: (failureCount, error) => {
			if (
				error instanceof ApiError &&
				error.statusCode > 0 &&
				error.statusCode < 500
			) {
				return false;
			}
			return failureCount < 2;
		},
	});

	return {
		isLoadingUser,
		isUserError,
		refetchUser,
		userData,
		userError,
	};
};
